/**
 * Feature Flags Service
 * Manages feature toggles for safe rollout of new features
 */

// Feature flag definitions
export const FEATURE_FLAGS = {
  RBAC_ADMIN_SCOPE_V1: 'rbac_admin_scope_v1',
  ENHANCED_NOTIFICATIONS: 'enhanced_notifications',
  ADVANCED_ANALYTICS: 'advanced_analytics'
};

// Default feature flag values
const DEFAULT_FLAGS = {
  [FEATURE_FLAGS.RBAC_ADMIN_SCOPE_V1]: false,
  [FEATURE_FLAGS.ENHANCED_NOTIFICATIONS]: true,
  [FEATURE_FLAGS.ADVANCED_ANALYTICS]: false
};

// In-memory cache for feature flags
let featureFlags = { ...DEFAULT_FLAGS };

/**
 * Initialize feature flags
 * In a real implementation, this would fetch from a remote config service
 */
export const initializeFeatureFlags = async () => {
  try {
    // For now, we'll use localStorage for client-side flags
    // In production, this would fetch from a remote config service
    const storedFlags = localStorage.getItem('featureFlags');
    if (storedFlags) {
      const parsedFlags = JSON.parse(storedFlags);
      featureFlags = { ...DEFAULT_FLAGS, ...parsedFlags };
    }
  } catch (error) {
    console.error('Error initializing feature flags:', error);
    // Fall back to defaults
    featureFlags = { ...DEFAULT_FLAGS };
  }
};

/**
 * Check if a feature flag is enabled
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} True if feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
  return featureFlags[flagName] === true;
};

/**
 * Set a feature flag value
 * @param {string} flagName - Name of the feature flag
 * @param {boolean} value - Flag value
 */
export const setFeatureFlag = (flagName, value) => {
  featureFlags[flagName] = value;
  
  // Persist to localStorage
  try {
    localStorage.setItem('featureFlags', JSON.stringify(featureFlags));
  } catch (error) {
    console.error('Error saving feature flags:', error);
  }
};

/**
 * Get all feature flags
 * @returns {Object} All feature flags
 */
export const getAllFeatureFlags = () => {
  return { ...featureFlags };
};

/**
 * Reset feature flags to defaults
 */
export const resetFeatureFlags = () => {
  featureFlags = { ...DEFAULT_FLAGS };
  localStorage.removeItem('featureFlags');
};

/**
 * Check if RBAC admin scope feature is enabled
 * @returns {boolean} True if RBAC admin scope is enabled
 */
export const isRBACEnabled = () => {
  return isFeatureEnabled(FEATURE_FLAGS.RBAC_ADMIN_SCOPE_V1);
};

/**
 * Check if enhanced notifications feature is enabled
 * @returns {boolean} True if enhanced notifications are enabled
 */
export const isEnhancedNotificationsEnabled = () => {
  return isFeatureEnabled(FEATURE_FLAGS.ENHANCED_NOTIFICATIONS);
};

/**
 * Check if advanced analytics feature is enabled
 * @returns {boolean} True if advanced analytics are enabled
 */
export const isAdvancedAnalyticsEnabled = () => {
  return isFeatureEnabled(FEATURE_FLAGS.ADVANCED_ANALYTICS);
};
